import { apiClient } from './apiClient';
import { authService } from './authService';

const getCompanyParam = () => {
  const companyId = authService.getCompanyId();
  return companyId ? `companyId=${companyId}` : '';
};

export const componentService = {
  // ==================== TEMPLATES ====================

  async getTemplates() {
    return apiClient.get(`/api/cmms/component-templates?${getCompanyParam()}`);
  },

  async getTemplate(id) {
    return apiClient.get(`/api/cmms/component-templates/${id}?${getCompanyParam()}`);
  },

  async createTemplate(data) {
    return apiClient.post(`/api/cmms/component-templates?${getCompanyParam()}`, data);
  },

  async updateTemplate(id, data) {
    return apiClient.put(`/api/cmms/component-templates/${id}?${getCompanyParam()}`, data);
  },

  async deleteTemplate(id) {
    return apiClient.delete(`/api/cmms/component-templates/${id}?${getCompanyParam()}`);
  },

  // ==================== ASSET COMPONENTS ====================

  async getAssetComponents(assetId) {
    return apiClient.get(`/api/cmms/assets/${assetId}/components?${getCompanyParam()}`);
  },

  async getComponent(id) {
    return apiClient.get(`/api/cmms/components/${id}?${getCompanyParam()}`);
  },

  async createComponent(assetId, data) {
    const payload = {
      templateId: data.templateId ? parseInt(data.templateId) : null,
      name: data.name,
      partNumber: data.partNumber || null,
      serialNumber: data.serialNumber || null,
      supplierId: data.supplierId ? parseInt(data.supplierId) : null,
      installationDate: data.installationDate || null,
      replacementCycleMonths: parseInt(data.replacementCycleMonths),
      alertDaysBefore: parseInt(data.alertDaysBefore) || 30,
      lastReplacementCost: data.lastReplacementCost ? parseFloat(data.lastReplacementCost) : null,
      warrantyMonths: data.warrantyMonths ? parseInt(data.warrantyMonths) : null,
      warrantyExpiration: data.warrantyExpiration || null,
      warrantyProvider: data.warrantyProvider || null,
      notes: data.notes || null,
      createAsset: data.registerAsAsset || false
    };
    return apiClient.post(`/api/cmms/assets/${assetId}/components?${getCompanyParam()}`, payload);
  },

  async updateComponent(id, data) {
    const payload = {
      templateId: data.templateId ? parseInt(data.templateId) : null,
      name: data.name,
      partNumber: data.partNumber || null,
      serialNumber: data.serialNumber || null,
      supplierId: data.supplierId ? parseInt(data.supplierId) : null,
      installationDate: data.installationDate || null,
      replacementCycleMonths: parseInt(data.replacementCycleMonths),
      alertDaysBefore: parseInt(data.alertDaysBefore) || 30,
      lastReplacementCost: data.lastReplacementCost ? parseFloat(data.lastReplacementCost) : null,
      warrantyMonths: data.warrantyMonths ? parseInt(data.warrantyMonths) : null,
      warrantyExpiration: data.warrantyExpiration || null,
      warrantyProvider: data.warrantyProvider || null,
      notes: data.notes || null
    };
    return apiClient.put(`/api/cmms/components/${id}?${getCompanyParam()}`, payload);
  },

  async deleteComponent(id) {
    return apiClient.delete(`/api/cmms/components/${id}?${getCompanyParam()}`);
  },

  // ==================== REPLACEMENT ====================

  async recordReplacement(componentId, data, workOrderId = null) {
    const payload = {
      componentId: parseInt(componentId),
      newPartNumber: data.newPartNumber || null,
      newSerialNumber: data.newSerialNumber || null,
      supplierId: data.supplierId ? parseInt(data.supplierId) : null,
      cost: data.cost ? parseFloat(data.cost) : null,
      notes: data.notes || null,
      replacementType: data.replacementType,
      replacedByUserId: data.replacedByUserId ? parseInt(data.replacedByUserId) : null,
      replacedBySupplierId: data.replacedBySupplierId ? parseInt(data.replacedBySupplierId) : null,
      replacedDate: data.replacedDate || null,
      warrantyMonths: data.warrantyMonths ? parseInt(data.warrantyMonths) : null,
      warrantyProvider: data.warrantyProvider || null
    };

    let url = `/api/cmms/components/${componentId}/replace?${getCompanyParam()}`;
    if (workOrderId) {
      url += `&workOrderId=${workOrderId}`;
    }

    return apiClient.post(url, payload);
  },

  async getComponentHistory(componentId) {
    return apiClient.get(`/api/cmms/components/${componentId}/history?${getCompanyParam()}`);
  },

  // ==================== DUE COMPONENTS ====================

  async getDueComponents() {
    return apiClient.get(`/api/cmms/components/due?${getCompanyParam()}`);
  },

  async getOverdueComponents() {
    return apiClient.get(`/api/cmms/components/overdue?${getCompanyParam()}`);
  },

  // ==================== ALERTS ====================

  async getAlerts(type = null) {
    let url = `/api/cmms/components/alerts?${getCompanyParam()}`;
    if (type) {
      url += `&type=${type}`;
    }
    return apiClient.get(url);
  },

  async getAlertSummary() {
    return apiClient.get(`/api/cmms/components/alerts/summary?${getCompanyParam()}`);
  },

  async acknowledgeAlert(alertId, userId) {
    return apiClient.post(`/api/cmms/components/alerts/${alertId}/acknowledge?${getCompanyParam()}&userId=${userId}`);
  }
};

// Component status constants
export const COMPONENT_STATUSES = [
  { value: 'ACTIVE', label: 'Active', color: 'green', bgClass: 'bg-green-100 text-green-800' },
  { value: 'DUE_SOON', label: 'Due Soon', color: 'yellow', bgClass: 'bg-yellow-100 text-yellow-800' },
  { value: 'OVERDUE', label: 'Overdue', color: 'red', bgClass: 'bg-red-100 text-red-800' },
  { value: 'INACTIVE', label: 'Inactive', color: 'gray', bgClass: 'bg-gray-100 text-gray-800' }
];

export const REPLACEMENT_TYPES = [
  { value: 'INTERNAL', label: 'Internal (Technician)' },
  { value: 'EXTERNAL', label: 'External (Supplier)' }
];

export const getStatusConfig = (status) => {
  return COMPONENT_STATUSES.find(s => s.value === status) || COMPONENT_STATUSES[0];
};
